import { devWarn } from "@/util/dev.mjs";

interface RetryOptions {
  maxRetries?: number;
  interval?: number;
  allowTrivialResult?: boolean;
}

const MAX_RETRY_IDLE_MS = 10 * 1000;
const EXP_BASE = 2;

/**
 * Pretty simple utility to retry promises with exponential backoff.
 */
function retry<T>(
  fn: () => Promise<T>,
  retryOptions: RetryOptions = {},
): Promise<T> {
  const {
    maxRetries = 1,
    interval = 200,
    allowTrivialResult = false,
  } = retryOptions;

  checkRetryIdleTime(maxRetries, interval);

  return new Promise((resolve, reject) => {
    let attempts = 0;

    function attempt() {
      attempts++;

      fn()
        .then((result) => {
          // Special case handling for trivial results...
          const isTrivial =
            result === undefined ||
            result === null ||
            (result as unknown as string) === "" ||
            (result as unknown as string) === "null";
          if (!allowTrivialResult && isTrivial)
            throw new Error("Trivial result");

          resolve(result);
        })
        .catch((error) => {
          if (attempts > maxRetries) {
            return reject(error);
          }

          // Exponentiate interval as the likelihood of each attempt failing increases.
          setTimeout(attempt, interval * Math.pow(EXP_BASE, attempts - 1));
        });
    }

    attempt();
  });
}

function checkRetryIdleTime(r, c) {
  let sum = 0;

  for (let i = 0; i < r; i++) {
    sum += c * Math.pow(EXP_BASE, i);
  }

  if (sum > MAX_RETRY_IDLE_MS) {
    devWarn(
      `Using ${r} retries with ${c}ms base interval would result in excessive max idle time of ${sum}ms. Choose smaller parameters.`,
    );
  }

  return sum;
}

export default retry;
